import React, { useEffect, useMemo, useState } from "react";
import {
    Box,
    Chip,
    FormControl,
    InputLabel,
    MenuItem,
    Select,
    ToggleButton,
    ToggleButtonGroup,
    Tooltip,
    Typography,
} from "@mui/material";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import {
    Area,
    AreaChart,
    Bar,
    BarChart,
    CartesianGrid,
    Legend,
    Line,
    LineChart,
    Tooltip as RechartsTooltip,
    XAxis,
    YAxis,
} from "recharts";
import {
    CHART_TYPES,
    SEASON_MODES,
    VALUE_MODES,
    buildChartData,
    formatMetricValue,
    getDefaultGraphPrefs,
    getEmptyChartMessage,
    getMetricsForPlayer,
    getUniqueLeagues,
    loadGraphPrefs,
    normalizeGraphPrefs,
    resolveSelectedMetrics,
    saveGraphPrefs,
} from "./nhlStatsGraphUtils";
import { useChartWidth } from "./useChartWidth";

const CHART_HEIGHT = 320;
const AXIS_COLOR = "#a1a8b3";

function ChartTooltip({ active, payload, label, metricIndex, valueMode }) {
    if (!active || !payload?.length) return null;

    return (
        <div className="nhl-stats-graph-tooltip">
            <div className="nhl-stats-graph-tooltip-title">{label}</div>
            {payload.map((entry) => {
                const metricId = entry.dataKey;
                const def = metricIndex[metricId];
                const suffix = valueMode === "per_game" && def?.perGame ? " / GP" : "";
                return (
                    <div key={metricId} className="nhl-stats-graph-tooltip-row">
                        <span className="nhl-stats-graph-tooltip-dot" style={{ backgroundColor: entry.color }} />
                        <span className="nhl-stats-graph-tooltip-label">{def?.label || metricId}{suffix}:</span>
                        <span className="nhl-stats-graph-tooltip-value">
                            {formatMetricValue(metricId, entry.value, metricIndex)}
                        </span>
                    </div>
                );
            })}
        </div>
    );
}

function renderSeries(chartType, metricId, name, color) {
    if (chartType === "bar") {
        return (
            <Bar
                key={metricId}
                dataKey={metricId}
                name={name}
                fill={color}
                stroke={color}
                fillOpacity={0.85}
                radius={[4, 4, 0, 0]}
                barSize={28}
                isAnimationActive={false}
            />
        );
    }
    if (chartType === "area") {
        return (
            <Area
                key={metricId}
                type="monotone"
                dataKey={metricId}
                name={name}
                stroke={color}
                fill={color}
                fillOpacity={0.18}
                strokeWidth={2}
                connectNulls={false}
                isAnimationActive={false}
            />
        );
    }
    return (
        <Line
            key={metricId}
            type="monotone"
            dataKey={metricId}
            name={name}
            stroke={color}
            strokeWidth={2.5}
            dot={{ r: 3, strokeWidth: 0, fill: color }}
            activeDot={{ r: 5 }}
            connectNulls={false}
            isAnimationActive={false}
        />
    );
}

function PlayerNhlStatsGraph({ stats, isGoalie, playerId }) {
    const [containerRef, chartWidth] = useChartWidth();
    const metrics = useMemo(() => getMetricsForPlayer(isGoalie), [isGoalie]);
    const metricIndex = useMemo(() => Object.fromEntries(metrics.map((m) => [m.id, m])), [metrics]);
    const leagues = useMemo(() => getUniqueLeagues(stats), [stats]);

    const [prefs, setPrefs] = useState(() =>
        normalizeGraphPrefs(loadGraphPrefs(playerId, isGoalie), isGoalie, stats)
    );

    useEffect(() => {
        setPrefs(normalizeGraphPrefs(loadGraphPrefs(playerId, isGoalie), isGoalie, stats));
    }, [playerId, isGoalie]);

    useEffect(() => {
        if (!stats.length) return;
        setPrefs((current) => normalizeGraphPrefs(current, isGoalie, stats));
    }, [stats, isGoalie]);

    useEffect(() => {
        saveGraphPrefs(playerId, prefs);
    }, [playerId, prefs]);

    const selectedMetrics = useMemo(
        () => resolveSelectedMetrics(prefs, isGoalie),
        [prefs, isGoalie]
    );

    const chartData = useMemo(
        () => buildChartData(stats, prefs, isGoalie),
        [stats, prefs, isGoalie]
    );

    const emptyChartMessage = useMemo(
        () => getEmptyChartMessage(prefs, stats),
        [prefs, stats]
    );

    const selectSx = {
        color: "#f3f4f6",
        "& .MuiOutlinedInput-notchedOutline": {
            borderColor: "var(--border-strong)",
        },
        "&:hover .MuiOutlinedInput-notchedOutline": {
            borderColor: "rgba(148, 192, 115, 0.65)",
        },
        "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
            borderColor: "rgba(148, 192, 115, 0.65)",
        },
        "& .MuiSvgIcon-root": {
            color: "#f3f4f6",
        },
    };

    const selectLabelSx = {
        color: "var(--text-secondary)",
        "&.Mui-focused": {
            color: "#f3f4f6",
        },
    };

    const menuPaperSx = {
        bgcolor: "var(--bg-surface-2)",
        border: "1px solid var(--border-strong)",
        "& .MuiMenuItem-root": {
            color: "#f3f4f6",
            fontSize: "0.85rem",
        },
        "& .MuiMenuItem-root.Mui-selected": {
            backgroundColor: "rgba(126, 168, 98, 0.18)",
        },
        "& .MuiMenuItem-root.Mui-selected:hover": {
            backgroundColor: "rgba(126, 168, 98, 0.24)",
        },
        "& .MuiMenuItem-root:hover": {
            backgroundColor: "rgba(255, 255, 255, 0.06)",
        },
    };

    const ChartComponent = prefs.chartType === "bar"
        ? BarChart
        : prefs.chartType === "area"
            ? AreaChart
            : LineChart;

    const updatePrefs = (patch) => {
        setPrefs((current) => normalizeGraphPrefs({ ...current, ...patch }, isGoalie, stats));
    };

    const toggleMetric = (metricId) => {
        setPrefs((current) => {
            const currentMetrics = resolveSelectedMetrics(current, isGoalie);
            const isSelected = currentMetrics.includes(metricId);
            if (isSelected && currentMetrics.length <= 1) {
                return current;
            }
            const nextMetrics = isSelected
                ? currentMetrics.filter((id) => id !== metricId)
                : [...currentMetrics, metricId];
            return normalizeGraphPrefs({ ...current, selectedMetrics: nextMetrics }, isGoalie, stats);
        });
    };

    const toggleLeague = (league) => {
        setPrefs((current) => {
            const selected = current.selectedLeagues || [];
            const isSelected = selected.includes(league);
            const next = isSelected ? selected.filter((l) => l !== league) : [...selected, league];
            return normalizeGraphPrefs({ ...current, selectedLeagues: next }, isGoalie, stats);
        });
    };

    const resetPrefs = () => {
        setPrefs(getDefaultGraphPrefs(isGoalie));
    };

    const hasRateMetric = selectedMetrics.some((id) => metricIndex[id]?.rate);
    const showPerGame = !hasRateMetric && selectedMetrics.some((id) => metricIndex[id]?.perGame);

    return (
        <div className="nhl-stats-graph">
            <div className="nhl-stats-graph-controls">
                <div className="nhl-stats-graph-control-row">
                    <Typography variant="caption" className="nhl-stats-graph-control-label">
                        Metrics
                    </Typography>
                    <Box className="nhl-stats-graph-chips">
                        {metrics.map((metric) => (
                            <Chip
                                key={metric.id}
                                label={metric.label}
                                size="small"
                                clickable
                                onClick={() => toggleMetric(metric.id)}
                                className={`nhl-stats-graph-chip${selectedMetrics.includes(metric.id) ? " nhl-stats-graph-chip--active" : ""}`}
                                style={
                                    selectedMetrics.includes(metric.id)
                                        ? { borderColor: metric.color, color: metric.color }
                                        : undefined
                                }
                            />
                        ))}
                    </Box>
                </div>

                {leagues.length > 1 && (
                    <div className="nhl-stats-graph-control-row">
                        <Typography variant="caption" className="nhl-stats-graph-control-label">
                            Leagues
                        </Typography>
                        <Box className="nhl-stats-graph-chips">
                            <Chip
                                label="All leagues"
                                size="small"
                                clickable
                                onClick={() => updatePrefs({ selectedLeagues: [] })}
                                className={`nhl-stats-graph-chip${!prefs.selectedLeagues?.length ? " nhl-stats-graph-chip--active" : ""}`}
                            />
                            {leagues.map((league) => (
                                <Chip
                                    key={league}
                                    label={league}
                                    size="small"
                                    clickable
                                    onClick={() => toggleLeague(league)}
                                    className={`nhl-stats-graph-chip${prefs.selectedLeagues?.includes(league) ? " nhl-stats-graph-chip--active" : ""}`}
                                />
                            ))}
                        </Box>
                    </div>
                )}

                <div className="nhl-stats-graph-control-row nhl-stats-graph-control-row--inline">
                    <FormControl size="small" className="nhl-stats-graph-select">
                        <InputLabel id="nhl-season-mode-label" sx={selectLabelSx}>
                            Seasons
                        </InputLabel>
                        <Select
                            labelId="nhl-season-mode-label"
                            value={prefs.seasonMode}
                            label="Seasons"
                            onChange={(e) => updatePrefs({ seasonMode: e.target.value })}
                            sx={selectSx}
                            MenuProps={{
                                PaperProps: {
                                    sx: menuPaperSx,
                                },
                            }}
                        >
                            {SEASON_MODES.map((mode) => (
                                <MenuItem key={mode.id} value={mode.id}>
                                    {mode.label}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    <ToggleButtonGroup
                        value={prefs.chartType}
                        exclusive
                        size="small"
                        onChange={(_, value) => value && updatePrefs({ chartType: value })}
                        className="nhl-stats-graph-toggle"
                    >
                        {CHART_TYPES.map((type) => (
                            <ToggleButton key={type.id} value={type.id}>
                                {type.label}
                            </ToggleButton>
                        ))}
                    </ToggleButtonGroup>

                    {showPerGame && (
                        <ToggleButtonGroup
                            value={prefs.valueMode}
                            exclusive
                            size="small"
                            onChange={(_, value) => value && updatePrefs({ valueMode: value })}
                            className="nhl-stats-graph-toggle"
                        >
                            {VALUE_MODES.map((mode) => (
                                <ToggleButton key={mode.id} value={mode.id}>
                                    {mode.label}
                                </ToggleButton>
                            ))}
                        </ToggleButtonGroup>
                    )}

                    <Tooltip title="Reset chart preferences">
                        <Chip
                            icon={<RestartAltIcon />}
                            label="Reset"
                            size="small"
                            clickable
                            onClick={resetPrefs}
                            className="nhl-stats-graph-chip nhl-stats-graph-chip--reset"
                        />
                    </Tooltip>
                </div>
            </div>

            {chartData.length > 0 ? (
                <div ref={containerRef} className="nhl-stats-graph-chart-wrap">
                    <ChartComponent
                        width={chartWidth}
                        height={CHART_HEIGHT}
                        data={chartData}
                        margin={{ top: 12, right: 16, left: 8, bottom: 8 }}
                    >
                        <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.08)" />
                        <XAxis
                            dataKey="label"
                            stroke={AXIS_COLOR}
                            tick={{ fill: AXIS_COLOR, fontSize: 11 }}
                            interval="preserveStartEnd"
                        />
                        <YAxis
                            stroke={AXIS_COLOR}
                            tick={{ fill: AXIS_COLOR, fontSize: 11 }}
                            width={48}
                        />
                        <RechartsTooltip
                            content={
                                <ChartTooltip metricIndex={metricIndex} valueMode={prefs.valueMode} />
                            }
                        />
                        <Legend wrapperStyle={{ fontSize: "0.75rem", paddingTop: 8 }} />
                        {selectedMetrics.map((metricId) =>
                            renderSeries(
                                prefs.chartType,
                                metricId,
                                metricIndex[metricId].label,
                                metricIndex[metricId].color
                            )
                        )}
                    </ChartComponent>
                </div>
            ) : (
                <Typography variant="body2" className="empty-tab-message nhl-stats-graph-empty">
                    {emptyChartMessage}
                </Typography>
            )}
        </div>
    );
}

export default PlayerNhlStatsGraph;
